import { useMemo } from 'react'
import styled from 'styled-components'
import ReactSelect, { components, Props as RSProps, GroupBase, MultiValue, ActionMeta } from 'react-select'

import { ellipsizeTokenAddress } from 'utils/helpers'
import { IconSearch } from 'components/Icons/IconSearch'
import { Stylable } from 'utils/types'
import { useIsMobile } from 'utils/useIsMobile'

export interface AutocompleteProps extends Stylable {
  options: string[]
  value: string[]
  onChange: (values: string[]) => void
  placeholder?: string
}

const DropdownIndicator = (props: any) => {
  const isMobile = useIsMobile()

  return (
    <components.DropdownIndicator {...props}>
      <IconSearch width={isMobile ? 16 : 24} height={isMobile ? 16 : 24} />
    </components.DropdownIndicator>
  )
}

type Option = { value: string; label: string }
type RSAutocompleteMultiProps = RSProps<Option, true, GroupBase<Option>>

const BaseAutocompleteMultiSelect = (props: RSAutocompleteMultiProps) => <ReactSelect {...props} />

const StyledSelect = styled(BaseAutocompleteMultiSelect)`
  width: 500px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    width: 100%;
  }

  .react-select__placeholder {
    color: ${({ theme }) => theme.colors.text.secondary} !important;
    font-size: ${({ theme }) => theme.typography.size.Description}px;
    text-align: left;
  }

  .react-select__control {
    // height: 40px;
    // min-height: 40px;
    border-radius: 999px;
    padding: 2px 10px;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
    cursor: text;
    &:hover {
      border: 1px solid ${({ theme }) => theme.colors.text.primary}; /* 👈 keep same */
    }

    @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
      height: 36px;
      min-height: 36px;
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
  }

  .react-select__control--is-focused {
    box-shadow: none !important;
    border-color: ${({ theme }) => theme.colors.text.primary} !important;
  }

  .react-select__indicator-separator {
    display: none;
  }
  .react-select__dropdown-indicator {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  .react-select__menu {
    background: ${({ theme }) => theme.colors.background.primary};
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
  }
  .react-select__option {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.size.Description}px;
    @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
      font-size: ${p => p.theme.typography.size.Caption1}px;
      text-align: left;
    }
  }
  .react-select__option--is-focused {
    background: ${({ theme }) => theme.colors.background.hover};
  }
  .react-select__option--is-selected {
    background: ${({ theme }) => theme.colors.background.hover};
  }

  .react-select__multi-value {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.text.primary};
    border-radius: 999px;
    font-size: ${({ theme }) => theme.typography.size.Description}px;
  }
  .react-select__multi-value__label {
    color: ${({ theme }) => theme.colors.text.primary};
    padding: 2px 8px;
  }
  .react-select__multi-value__remove {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.primary};
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;

    &:hover {
      background: transparent;
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }

  .react-select__single-value,
  .react-select__placeholder,
  .react-select__input-container {
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.typography.size.Description}px;

    @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
      font-size: ${p => p.theme.typography.size.Caption1}px;
    }
  }

  .react-select__menu,
  .react-select__option {
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }

  .react-select__option:active,
  .react-select__option--is-focused:active,
  .react-select__option--is-selected:active {
    background: ${({ theme }) => theme.colors.background.hover};
  }

  .react-select__value-container {
    height: 100%;
    padding: 0 6px;
  }

  .react-select__input-container {
    margin: 0;
    padding: 0;
  }

  .react-select__indicators {
    height: 100%;
  }
`

export const SimpleAutocomplete = ({
  options,
  value,
  onChange,
  placeholder = 'Search by wallet',
}: AutocompleteProps) => {
  const handleChange = (newValue: MultiValue<Option>, _meta: ActionMeta<Option>) => {
    onChange(newValue.map(o => o.value))
  }

  const optionsToUse = useMemo(() => {
    if (value.length === 0) {
      return [...options]
    }
    return options.filter(o => o === value[0])
  }, [options, value])

  return (
    <StyledSelect
      classNamePrefix="react-select"
      isMulti
      isSearchable
      isClearable={false}
      closeMenuOnSelect={true}
      hideSelectedOptions={false}
      backspaceRemovesValue
      openMenuOnFocus={false}
      openMenuOnClick={false}
      options={optionsToUse.map(addr => ({ value: addr, label: addr }))}
      value={value.map(addr => ({ value: addr, label: addr }))}
      onChange={handleChange}
      formatOptionLabel={opt => ellipsizeTokenAddress(opt.value)}
      placeholder={placeholder}
      components={{ DropdownIndicator, IndicatorSeparator: () => null }}
      filterOption={(candidate, input) => candidate.data.value.toLowerCase() === input.trim().toLowerCase()}
    />
  )
}
